import CheckIcon from "@mui/icons-material/Check";
import { Button } from "@mui/material";
import { useToast } from "@qubit/autoparts";
import { AxiosError } from "axios";
import dayjs from "dayjs";
import { useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

import { useAppDispatch, useAppSelector } from "~/app/store";
import useFlag from "~/config/flags";
import { useCantInductReasons } from "~/hooks/useCantInductReasons";
import { useIsInventoryDateValid } from "~/hooks/useIsInventoryDateValid";
import { usePortStatusQuery } from "~/hooks/usePortStatus";
import { useShouldListenToGridEvents } from "~/hooks/useShouldListenToGridEvents";

import {
  getInventoryDateObj,
  getAxiosErrorMessage,
  checkIsExpiration
} from "~/lib/helpers";
import { mixpanelTrack, useMixpanelPageName } from "~/lib/mixpanel-tracking";
import { nullIfUndefinedOrEmpty } from "~/lib/notNullOrUndefined";
import { getMessageFromRtkError } from "~/lib/rtkErrorToMessage";
import { nextEmptyBin, fetchPortStatus } from "~/redux/actions";
import { selectServerConfig } from "~/redux/selectors/appSelectors";
import {
  selectCurrentlySelectedBinId,
  selectSelectedRow
} from "~/redux/selectors/autostorePutawaySelectors";
import { selectThisWorkstation } from "~/redux/selectors/workstationsSelectors";
import { usePatchInventoryNetAdjustmentMutation } from "~/redux/warehouse/inventory.hooks";
import {
  usePatchDecantingRateMutation,
  usePostPutItemAwayMutation
} from "~/redux/warehouse/putAwayTasks.hooks";
import {
  Guid,
  MeasuredValueDto,
  PutAwayItemRequest,
  PutAwayTaskSummaryDto,
  SetDecantingRateRequest
} from "~/types/api";

import {
  setSearchData,
  setIsPutawayTaskTableRefreshed,
  setPortPollingActive,
  setClosePortBtnDisabled,
  reset,
  selectLotNumber,
  selectIsConfirmInductionButtonDisabled,
  setPreviousPutawayTask,
  selectIsProductTooLargeForAllBins,
  removeSanitizedBin
} from "./autostorePutaway.slice";
import { useGetPutawayTasks } from "./useGetPutawayTasks";

type DecantingInfo = {
  putAwayTaskId: Guid;
  decantingRate?: MeasuredValueDto;
  shouldForceUpdateDecantingRate?: boolean;
};

export function ConfirmInductionButton() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { errorToast, successToast } = useToast();
  const intervalRef = useRef(-1);
  const shouldListenToGridEvents = useShouldListenToGridEvents();
  const trackedPageName = useMixpanelPageName();

  const searchData = useAppSelector(
    (state) => state.autostorePutaway.searchData
  );
  const pageLimit = 7;
  const { putawayTasks } = useGetPutawayTasks(pageLimit, searchData);

  const selectedRow = useAppSelector((state) =>
    selectSelectedRow(state, putawayTasks)
  );
  const { isPutawayTaskIneligible } = useCantInductReasons(selectedRow);
  const currentSelectedBin = useAppSelector(
    (state) => state.autostore.currentEmptyBin
  );
  const selectedCompartment = useAppSelector(
    (state) => state.autostorePutaway.selectedCompartment
  );
  const selectedCompartmentBinId = useAppSelector(selectCurrentlySelectedBinId);
  const sanitizedBins = useAppSelector(
    (state) => state.autostorePutaway.sanitizedBins
  );
  const changedQuantity = useAppSelector(
    (state) => state.autostorePutaway.changedQuantity
  );
  const siteWorkstation = useAppSelector(selectThisWorkstation);
  const { inv_inventoryDateLabel, putaway_multipleSearchTerms } =
    useAppSelector(selectServerConfig);
  const inventoryDate = useAppSelector(
    (state) => state.autostorePutaway.inventoryDate
  );
  const lotNumber = useAppSelector(selectLotNumber);
  const closePortBtnDisabled = useAppSelector(
    (state) => state.autostorePutaway.closePortBtnDisabled
  );
  const isConfirmInductionButtonDisabled = useAppSelector(
    selectIsConfirmInductionButtonDisabled
  );
  const isProductTooLargeForAllBins = useAppSelector(
    selectIsProductTooLargeForAllBins
  );

  const isProductTargetedForSanitization =
    selectedRow?.product.isSanitizeTargetBin;
  const isBinSanitized =
    currentSelectedBin?.openBinResponse.binId &&
    sanitizedBins.includes(currentSelectedBin?.openBinResponse.binId);
  const isBinSanitizedOrNotProductTargetedForSanitization =
    isBinSanitized || !isProductTargetedForSanitization;

  const isInventoryDateValid = useIsInventoryDateValid(putawayTasks);

  const currentSelectedPortId = currentSelectedBin?.openBinResponse.portId;
  const { isReady: isPortReady, isBinOpen } = usePortStatusQuery(
    currentSelectedPortId || undefined
  );

  const [patchDecantingRate, { isLoading: isUpdatingDecantingRate }] =
    usePatchDecantingRateMutation();
  const [postPutItemAway, { isLoading: isPuttingItemAway }] =
    usePostPutItemAwayMutation();
  const [adjustInventoryNet] = usePatchInventoryNetAdjustmentMutation();

  // disable confirm button if selected product is a food safety item and user has a non-whole bin selected
  const isFoodSafetyItem = selectedRow?.product.isFoodSafetyItem;
  const isWholeBin =
    currentSelectedBin?.autostoreBinConfiguration?.configurationType === 1;
  const disabledForFoodSafety = isFoodSafetyItem && !isWholeBin;

  const { adjustOnPutawayTask: isAdjustOnPutawayTaskEnabled } = useFlag();

  // clear the interval on unmount, if it is set
  useEffect(() => {
    return () => {
      if (intervalRef.current != -1) {
        clearInterval(intervalRef.current);
        intervalRef.current = -1;
      }
    };
  }, []);

  /**
   * Determine if we need to adjust the inventory record in addition to putting the item away.
   * If the lot number or expiration/manufacture date is changing,
   * we need to adjust the inventory record because those fields are on the inventory record
   * and not the putaway task.
   *
   * @param inventoryDateConfiguration The inventory date configuration for the site, which determines whether we are using expiration date or manufacture date
   * @param selectedRow The currently selected putaway task row
   * @param putAwayItemArgs The arguments for the putAwayItem API call
   *
   * @returns A boolean indicating whether the inventory record should be adjusted
   */
  const shouldAdjust = (
    inventoryDateConfiguration: string,
    selectedRow: PutAwayTaskSummaryDto | undefined,
    putAwayItemArgs: PutAwayItemRequest
  ) => {
    // Lot Number
    const currentLotNumber = nullIfUndefinedOrEmpty(selectedRow?.lotNumber);
    const targetLotNumber = nullIfUndefinedOrEmpty(
      putAwayItemArgs.targetLotNumber
    );

    if (currentLotNumber !== (targetLotNumber ?? currentLotNumber)) {
      return true;
    }

    if (checkIsExpiration(inventoryDateConfiguration)) {
      // Expiration Date
      const currentExpiration = nullIfUndefinedOrEmpty(
        selectedRow?.expirationDate
      );
      const targetExpiration = nullIfUndefinedOrEmpty(
        putAwayItemArgs.targetExpiration
      );

      return currentExpiration !== (targetExpiration ?? currentExpiration);
    } else {
      // Manufacture Date
      const currentManufactureDate = nullIfUndefinedOrEmpty(
        selectedRow?.manufactureDate
      );
      const targetManufactureDate = nullIfUndefinedOrEmpty(
        putAwayItemArgs.targetManufactureDate
      );

      return (
        currentManufactureDate !==
        (targetManufactureDate ?? currentManufactureDate)
      );
    }
  };

  const putAwayItem = useCallback(
    async (
      decantingArgs: DecantingInfo,
      putAwayItemArgs: PutAwayItemRequest
    ) => {
      const { putAwayTaskId, decantingRate, shouldForceUpdateDecantingRate } =
        decantingArgs;
      const { quantity } = putAwayItemArgs;
      const decantingRateRequest: SetDecantingRateRequest = { quantity };

      if (
        (decantingRate && decantingRate?.value !== quantity.value) ||
        shouldForceUpdateDecantingRate
      ) {
        await patchDecantingRate({
          putAwayTaskId,
          request: decantingRateRequest
        }).unwrap();
      }

      await postPutItemAway({
        putAwayTaskId,
        request: putAwayItemArgs
      }).unwrap();

      const inventoryId = selectedRow?.inventoryId;
      if (
        isAdjustOnPutawayTaskEnabled &&
        inventoryId &&
        shouldAdjust(inv_inventoryDateLabel, selectedRow, putAwayItemArgs)
      ) {
        try {
          await adjustInventoryNet({
            inventoryId: inventoryId,
            netAdjustment: { ...quantity, value: 0 },
            expiration: putAwayItemArgs.targetExpiration,
            manufactureDate: putAwayItemArgs.targetManufactureDate,
            lotNumber: putAwayItemArgs.targetLotNumber,
            reasonCode: "Putaway Corrected",
            workstationId: siteWorkstation?.id
          }).unwrap();
        } catch {
          // An adjustment failure here is low stakes, as it just means the
          // user has to re-enter the same data again, which is fine.
          // The important thing here was the Putaway succeeding.
        }
      }
    },
    [
      patchDecantingRate,
      postPutItemAway,
      adjustInventoryNet,
      siteWorkstation?.id,
      selectedRow,
      isAdjustOnPutawayTaskEnabled,
      inv_inventoryDateLabel
    ]
  );

  const handleConfirmPutaway = useCallback(async () => {
    if (!selectedRow || !selectedCompartmentBinId) return;

    const quantityValue = {
      value: changedQuantity || selectedRow.remaining.value,
      units: selectedRow.remaining.units
    };

    const dateObject = getInventoryDateObj(
      inv_inventoryDateLabel,
      inventoryDate ? dayjs(inventoryDate) : null
    );

    const putAwayItemArgs: PutAwayItemRequest = {
      quantity: quantityValue,
      targetBinId: selectedCompartmentBinId,
      targetPort: {
        gridId: siteWorkstation?.autostoreGridId || "",
        portId: currentSelectedPortId || 0
      },
      targetLotNumber: lotNumber,
      workstationId: siteWorkstation?.id,
      ...(isProductTargetedForSanitization && {
        wasTargetBinSanitized: !!isBinSanitized
      }),
      targetManufactureDate: dateObject.manufactureDate,
      targetExpiration: dateObject.expiration
    };

    const decantingArgs: DecantingInfo = {
      putAwayTaskId: selectedRow.putAwayTaskId,
      decantingRate: selectedRow.decantingRate,
      // If the product currently has no decant rate, we want to save the quantity as the
      // decant rate for the future
      shouldForceUpdateDecantingRate: !selectedRow?.decantingRate
    };

    try {
      await putAwayItem(decantingArgs, putAwayItemArgs);

      successToast(t("putaway complete"));
      dispatch(reset());
      dispatch(
        setPreviousPutawayTask({
          ...selectedRow,
          ...(dateObject.expiration
            ? { expirationDate: dateObject.expiration }
            : dateObject),
          lotNumber,
          quantity: quantityValue,
          binId: currentSelectedBin?.openBinResponse.binId || 0,
          portId: currentSelectedBin?.openBinResponse.portId || 0,
          compartment: selectedCompartment || 0
        })
      );
      // clear sanitized bin after putaway complete
      if (
        isProductTargetedForSanitization &&
        currentSelectedBin?.openBinResponse.binId
      ) {
        dispatch(removeSanitizedBin(currentSelectedBin?.openBinResponse.binId));
      }
    } catch (err) {
      errorToast(getMessageFromRtkError(err));
      return;
    }

    if (putaway_multipleSearchTerms)
      dispatch(setSearchData({ ...searchData, scannedBarcodes: [] }));

    // TODO: This is essentially copy/pasted from fetchNextEmptyBin in useStartInduction.ts.
    //       Prime candidate to be refactored and consolidated.
    // For now, we don't have something like 'close bin' event
    // So there is no way to know when this operation will be completed
    // Because of that, we periodically check if bin is closed
    const fetchNextEmptyBin = () => {
      const fetchBinPoll = async () => {
        const portStatus = await dispatch(
          fetchPortStatus({
            portId: currentSelectedPortId
          })
        );

        if (
          portStatus &&
          portStatus.mode === "OPEN" &&
          portStatus.selectedBin === 0
        ) {
          clearInterval(intervalRef.current);
          intervalRef.current = -1;
          try {
            await dispatch(
              nextEmptyBin({
                portId: currentSelectedBin?.openBinResponse.portId
              })
            );
          } catch (err: unknown) {
            errorToast(
              getAxiosErrorMessage(err as AxiosError) ??
                "No more empty bins found"
            );
            if (portStatus.mode === "OPEN") {
              if (!shouldListenToGridEvents) {
                dispatch(setPortPollingActive(false));
              }
              if (!closePortBtnDisabled) {
                dispatch(setClosePortBtnDisabled(true));
              }
            }
          }

          if (!shouldListenToGridEvents) {
            dispatch(setPortPollingActive(true));
          }
        }
      };
      // Call fetchBinPoll immediately, then set an interval to call it
      // repeatedly in case the bin is not closed yet
      void fetchBinPoll();
      intervalRef.current = window.setInterval(() => {
        void fetchBinPoll();
      }, 500);
    };

    fetchNextEmptyBin();

    // last task on selected page but not a partial putaway - clear search, refresh list
    if (
      putawayTasks.length === 1 &&
      !(changedQuantity && changedQuantity !== selectedRow.remaining.value)
    ) {
      if (searchData.offset !== 1) {
        // user is not on first page, send them back a page
        dispatch(
          setSearchData({ ...searchData, offset: searchData.offset - 1 })
        );
      } else {
        // user is on first page, clear search, then refetch putaways
        if (searchData.searchInputValue || searchData.scannedBarcodes.length) {
          dispatch(
            setSearchData({
              scannedBarcodes: [],
              offset: 1
            })
          );
        }
        dispatch(setIsPutawayTaskTableRefreshed(true));
      }
      // not the last task or any type of partial putaway - keep search term, refetch results
    }

    return () => {
      clearInterval(intervalRef.current);
    };
  }, [
    changedQuantity,
    closePortBtnDisabled,
    currentSelectedBin?.openBinResponse.portId,
    currentSelectedBin?.openBinResponse.binId,
    currentSelectedPortId,
    dispatch,
    errorToast,
    inv_inventoryDateLabel,
    inventoryDate,
    lotNumber,
    putAwayItem,
    putawayTasks.length,
    putaway_multipleSearchTerms,
    searchData,
    selectedCompartmentBinId,
    selectedRow,
    shouldListenToGridEvents,
    siteWorkstation?.autostoreGridId,
    siteWorkstation?.id,
    successToast,
    isBinSanitized,
    isProductTargetedForSanitization,
    selectedCompartment,
    t
  ]);

  const isLotNumberValid =
    !!lotNumber || !selectedRow?.product.isLotNumberRequired;

  const isConfirmButtonEnabled =
    !!selectedRow &&
    selectedCompartment !== undefined &&
    !!currentSelectedPortId &&
    (isPortReady || isBinOpen) &&
    isInventoryDateValid &&
    !!changedQuantity &&
    isLotNumberValid &&
    !isConfirmInductionButtonDisabled &&
    !isPuttingItemAway &&
    !isUpdatingDecantingRate &&
    !disabledForFoodSafety &&
    !isProductTooLargeForAllBins &&
    isBinSanitizedOrNotProductTargetedForSanitization &&
    !isPutawayTaskIneligible;

  return (
    <Button
      style={{ width: "100%" }}
      size="large"
      startIcon={<CheckIcon />}
      onClick={() => {
        mixpanelTrack({
          trackedPageName,
          type: "Button Click",
          label: "Confirm Induction",
          eventProperties: {
            Product: selectedRow?.product,
            BinId: currentSelectedBin?.openBinResponse.binId,
            Compartment: selectedCompartment,
            Quantity: changedQuantity
          }
        });
        void handleConfirmPutaway();
      }}
      disabled={!isConfirmButtonEnabled}
    >
      {t("confirm induction")}
    </Button>
  );
}
